import { createControlId } from '@avantia/questionset-model';
import { HpSvgIconTypes, SvgIcon } from '@avantia/style-guide';
import jQuery from 'jquery';

import { HpClientQuestion } from '../../types';
import { HpOnClickEvent } from '../../types/inputControlTypes';
import { createSpaceKeyAsClickHandler } from '../formControls/controlEvents';
import { HtmlWidget } from './htmlWidget';

export interface HpHelpInfoIconProps {
  onClick: HpOnClickEvent;
  question: HpClientQuestion;
}

export function HelpIconWidget({ onClick, question }: HpHelpInfoIconProps): JSX.Element {
  const onClickFunc = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    const keepFocusVisible = (event as any).keepFocusVisible;
    if (!keepFocusVisible) {
      // Firefox currently sets ':focus-visible' on clicks (whilst Chrome etc only does so for key presses).
      // This attempts to bring FireFox a little closer to Chrome, but it's not perfect (e.g. when using a
      // mix of clicks and key presses, the ':focus-visible' is sometimes incorrectly disabled).
      jQuery(event.target).closest('.hp-help-text-icon').addClass('hide-focus-visible');
    }

    onClick(event, {
      type: 'help-icon',
      question
    });
  };

  const onKeyPressFunc = createSpaceKeyAsClickHandler(onClickFunc);
  return (
    <span
      id={createControlId({ questionId: question.questionId, options: { customPrefix: 'HI' } })}
      className="hp-help-text-icon"
      onKeyPress={onKeyPressFunc}
      onClick={onClickFunc}
      tabIndex={0}
    >
      <SvgIcon type="tooltip" size="auto" />
    </span>
  );
}

export interface HpHelpWidgetProps {
  helpText: string;
  isHelpIconHidden?: boolean;
}

export function HelpWidget({ helpText, isHelpIconHidden }: HpHelpWidgetProps): JSX.Element {
  return <GenericWidget type="help" text={helpText} isIconHidden={isHelpIconHidden} />;
}

export interface HpInfoWidgetProps {
  infoText: string;
  infoTitle?: string;
  isInfoIconHidden?: boolean;
}

export function InfoWidget({ infoText, infoTitle, isInfoIconHidden }: HpInfoWidgetProps): JSX.Element {
  return <GenericWidget type="info" text={infoText} title={infoTitle} isIconHidden={isInfoIconHidden} />;
}

export interface HpWarningWidgetProps {
  warningText: string;
  warningTitle?: string;
}

export function WarningWidget({ warningText, warningTitle }: HpWarningWidgetProps): JSX.Element {
  return <GenericWidget type="warning" text={warningText} title={warningTitle} />;
}

export interface HpGenericWidgetProps {
  isIconHidden?: boolean;
  text: string;
  title?: string;
  type: 'help' | 'info' | 'warning';
}

function GenericWidget({ isIconHidden, text, title, type }: HpGenericWidgetProps): JSX.Element {
  const htmlElement = <HtmlWidget className={`hp-${type}-text`} html={text} />;
  const widgetBody = title ? (
    <div className="hp-title-and-body">
      <div className={`hp-${type}-title`}>{title}</div>
      {htmlElement}
    </div>
  ) : (
    htmlElement
  );

  let iconType: HpSvgIconTypes = type as HpSvgIconTypes;
  switch (type) {
    case 'help':
      iconType = 'tooltip';
      break;
  }

  return (
    <div className={`hp-${type}-container${isIconHidden ? ` hp-${type}-icon-hidden` : ''}`}>
      {isIconHidden !== true && <SvgIcon type={iconType} />}
      {widgetBody}
    </div>
  );
}
